"use client";

// Dual-Cursor im Stil von relyz.ch:
// - Kleiner Dot folgt der Maus instant
// - Größerer Ring folgt mit sanftem Lerp-Delay (butterweich)
// - Ring wächst über klickbaren Elementen (Links, Buttons, Slider, Tabs)
// - mix-blend-mode: difference → bleibt auf hell und dunkel sichtbar
// - Nur auf Devices mit feinem Pointer (Desktop-Maus, kein Touch)

import { useEffect, useRef, useState } from "react";

export function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  // Zielposition (Maus) und aktuelle Ring-Position (läuft hinterher)
  const mouseRef = useRef({ x: -100, y: -100 });
  const ringRef2 = useRef({ x: -100, y: -100 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    // Nur auf Geräten mit echtem Hover + feinem Pointer (Desktop-Maus/Trackpad).
    // Schliesst Touch, iPad mit Stylus und die meisten Hybrid-Modi aus.
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!mq.matches) return;

    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    const onMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      if (dotRef.current) {
        // Dot folgt sofort
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
    };

    // Interactive-Hover — Ring wächst
    const HOVER_SEL = 'a, button, [role="button"], [role="slider"], [role="tab"], input, textarea, select, summary, [data-cursor-hover]';

    const onOver = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;
      const interactive = target.closest(HOVER_SEL);
      if (interactive && ringRef.current) {
        ringRef.current.classList.add("is-hover");
      }
    };

    const onOut = (e: Event) => {
      const target = e.target as Element | null;
      if (!target) return;
      const interactive = target.closest(HOVER_SEL);
      if (interactive && ringRef.current) {
        // Bei mouseout vom äußersten interaktiven Vorfahr abschalten
        const related = (e as MouseEvent).relatedTarget as Element | null;
        if (!related || !related.closest(HOVER_SEL)) {
          ringRef.current.classList.remove("is-hover");
        }
      }
    };

    // Bei Fenster-Leave Cursor-Elemente ausblenden (verhindert Ghost-Cursor)
    const onLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (dotRef.current) dotRef.current.style.opacity = "1";
      if (ringRef.current) ringRef.current.style.opacity = "1";
    };

    // Lerp-Loop für den Ring
    const tick = () => {
      const { x: mx, y: my } = mouseRef.current;
      const { x: rx, y: ry } = ringRef2.current;
      const ease = 0.18;
      ringRef2.current.x = rx + (mx - rx) * ease;
      ringRef2.current.y = ry + (my - ry) * ease;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringRef2.current.x}px, ${ringRef2.current.y}px) translate(-50%, -50%)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      cancelAnimationFrame(rafRef.current);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      {/* Ring — verzögert */}
      <div
        ref={ringRef}
        aria-hidden
        className="custom-cursor-ring"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 36,
          height: 36,
          border: "1.5px solid white",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 9999,
          mixBlendMode: "difference",
          transform: "translate(-100px, -100px)",
          transition:
            "width 220ms cubic-bezier(0.2, 0.8, 0.2, 1), height 220ms cubic-bezier(0.2, 0.8, 0.2, 1), border-color 200ms, opacity 200ms",
        }}
      />
      {/* Dot — instant */}
      <div
        ref={dotRef}
        aria-hidden
        className="custom-cursor-dot"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 6,
          height: 6,
          background: "white",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 9999,
          mixBlendMode: "difference",
          transform: "translate(-100px, -100px)",
          transition: "opacity 200ms",
        }}
      />
      <style jsx global>{`
        /* Native Cursor nur ausblenden, wenn Custom-Cursor aktiv (also auf Fein-Pointer) */
        html.has-custom-cursor,
        html.has-custom-cursor * {
          cursor: none !important;
        }
        /* Text-Inputs behalten Text-Caret-Cursor */
        html.has-custom-cursor input,
        html.has-custom-cursor textarea,
        html.has-custom-cursor [contenteditable="true"] {
          cursor: text !important;
        }
        /* Hover-Vergrößerung */
        .custom-cursor-ring.is-hover {
          width: 60px !important;
          height: 60px !important;
        }
      `}</style>
    </>
  );
}
